import { Stack } from "@mui/material";
import { SwipeableShiftCalendar } from "@src/appV2/redesign/Shift/Calendar/SwipeableShiftCalendar";
import { useShiftDiscoveryCalendarData } from "@src/appV2/redesign/Shift/Calendar/useShiftDiscoveryCalendarData";
import { endOfMonth, startOfDay } from "date-fns";
import { useEffect, useState } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";

import { ShiftTimeSlotButtonGroup } from "../ShiftTimeSlotButtonGroup";
import { useShiftDiscoveryUserFiltersContext } from "../useUserFiltersContext";
import type { AccordionItemProps } from "./AccordionCard";
import { getWhenFilterSummary } from "./filterSummaries";
import { type FiltersFormValues } from "./types";

interface WhenFilterComponentProps extends AccordionItemProps {
  form: UseFormReturn<FiltersFormValues>;
}

export function WhenFilterComponent(props: WhenFilterComponentProps) {
  const { form, onDescriptionChange } = props;

  const { setDates: setFiltersDates, dates } = useShiftDiscoveryUserFiltersContext();

  const {
    workerShiftsByDate,
    filteredOpenShiftsCountData,
    dateRange,
    openShiftsCountIsLoading,
    workerShiftsIsLoading,
    loadMore,
  } = useShiftDiscoveryCalendarData({
    initialDateRange: {
      startDate: startOfDay(new Date()),
      endDate: endOfMonth(new Date()),
    },
    monthByMonthLoading: true,
  });

  const isLoading = openShiftsCountIsLoading || workerShiftsIsLoading;

  const { startDate, endDate } = dateRange;

  const [selectedDates, setSelectedDates] = useState<string[]>(dates);

  // Sync local selectedDates with global dates when they change (e.g., when clearDates() is called)
  useEffect(() => {
    setSelectedDates(dates);
    onDescriptionChange?.(getWhenFilterSummary(form.getValues("shiftTimeSlots"), dates));
  }, [dates, form, onDescriptionChange]);

  const toggleDateSelection = (date: string, isSelected: boolean) => {
    setSelectedDates((previousSelectedDates) => {
      const newSelectedDates = isSelected
        ? [...previousSelectedDates, date]
        : previousSelectedDates.filter((selectedDate) => selectedDate !== date);

      setFiltersDates(newSelectedDates);
      onDescriptionChange?.(
        getWhenFilterSummary(form.getValues("shiftTimeSlots"), newSelectedDates)
      );

      return newSelectedDates;
    });
  };

  return (
    <Stack spacing={4}>
      <Stack sx={{ px: 5, pt: 6 }}>
        <Controller
          control={form.control}
          name="shiftTimeSlots"
          render={({ field }) => (
            <ShiftTimeSlotButtonGroup
              showAllButton
              value={field.value}
              onChange={(shiftTimeSlots) => {
                field.onChange(shiftTimeSlots);
                onDescriptionChange?.(getWhenFilterSummary(shiftTimeSlots, dates));
              }}
            />
          )}
        />
      </Stack>

      <SwipeableShiftCalendar
        startDate={startDate}
        endDate={endDate}
        isLoading={isLoading}
        filteredOpenShiftsCountData={filteredOpenShiftsCountData}
        workerShiftsByDate={workerShiftsByDate}
        selectedDates={selectedDates}
        loadMore={loadMore}
        showIndicatorDots={false}
        onDateToggle={toggleDateSelection}
      />
    </Stack>
  );
}
